import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { ProductCard } from "@/pages/Marketplace";

export default function Wishlist() {
  const { user, loading } = useAuth();
  const { has } = useWishlist();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/wishlist").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [user]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#df1721]" /></div>;

  if (!user) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-24 min-h-screen text-center" data-testid="wishlist-signin">
        <Heart size={40} className="mx-auto text-[#df1721] mb-4" />
        <h1 className="font-display font-extrabold text-3xl">Your wishlist lives here</h1>
        <p className="text-zinc-400 mt-2">Sign in to save products you love and get nudged when prices drop.</p>
        <Link to="/login" className="inline-block mt-6 bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-6 py-2.5 rounded-full transition-colors" data-testid="wishlist-login-cta">Sign in</Link>
      </div>
    );
  }

  const visible = (items || []).filter((p) => has(p.id));

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="wishlist-page">
      <div className="max-w-3xl mb-10">
        <p className="overline text-[#df1721]">Saved for later</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">My Wishlist</h1>
        <p className="text-zinc-400 mt-3">Everything you've hearted across the marketplace. We'll email you if a price drops.</p>
      </div>
      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#df1721]" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl" data-testid="wishlist-empty">
          <Heart size={36} className="mx-auto text-zinc-600 mb-3" />
          <p className="font-bold">Nothing saved yet</p>
          <p className="text-sm text-zinc-500 mt-1">Tap the heart on any product to keep it here.</p>
          <Link to="/marketplace" className="inline-block mt-5 text-sm font-bold text-[#df1721] hover:underline" data-testid="wishlist-browse-cta">Browse the marketplace →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="wishlist-grid">
          {visible.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}
